import axios from 'axios'
import store from '../helpers/store'
import * as config from 'config'
import { userActions } from '../actions'
import { toastActions } from './../actions/toast.actions'
import { ClientService } from './client.service'
import { AuthenticationService } from './authentication.service'
import { Creators as SiteCreators } from '../ducks/site/site'
import { REQUEST_CONTANTS } from '../constants'
import { history } from '../helpers/history'
import { routes } from '../constants/routes.constants'

const pendingRequests = {}

function getDeviceId() {
	const storageKey = 'device-id'
	let deviceId = localStorage.getItem(storageKey)

	if (!deviceId) {
		deviceId = `web-client-${Date.now()}-${Math.random()
			.toString(36)
			.substring(2, 15)}`
		localStorage.setItem(storageKey, deviceId)
	}

	return deviceId
}

function getBaseConfig() {
	const commonHeaders = {
		platform: REQUEST_CONTANTS.PLATFORM,
		agency: REQUEST_CONTANTS.AGENCY,
		'app-id': REQUEST_CONTANTS.APP_ID,
		'app-description': REQUEST_CONTANTS.APP_DESCRIPTION,
		so: '0',
		'device-id': getDeviceId()
	}

	const user = store.getState().user

	if (!user) return { headers: commonHeaders }

	return {
		headers: {
			Authorization: `Bearer ${user.accessToken}`,
			Pragma: 'no-cache',
			...commonHeaders
		}
	}
}

const loaderIgnoredPaths = [
	'/',
	'/regulamento',
	'/perguntas-frequentes',
	'/politica-de-privacidade'
]

const toastIgnorePaths = ['/cliente/compra-de-passagem', '/compra-de-passagem']

function handleError(error) {
	if (axios.isCancel(error)) return Promise.reject(error)

	const loaderEnabled = !loaderIgnoredPaths.includes(location.pathname)
	loaderEnabled && store.dispatch(SiteCreators.showLoading())
	const user = store.getState().user
	return new Promise((resolve, reject) => {
		if (error.response && error.response.status === 401 && user) {
			AuthenticationService.refreshToken(user.id, user.refreshToken)
				.then(({ success, data }) => {
					if (!success) {
						store.dispatch(userActions.logout())
						loaderEnabled && store.dispatch(SiteCreators.hideLoading())
						reject(error)
					
						if (location.pathname.includes(`cliente${routes.COMPRA_DE_PASSAGEM}`)
						) {
							history.push(routes.COMPRA_DE_PASSAGEM)
							return
						}

						history.push('/entrar', {
							redirectUrl: location.pathname
						})

						return	
					}

					store.dispatch(userActions.login(data))

					ClientService.getSubscription().then(
						({ success, ...response }) => {
							if (success && response.data) {
								data.plan = response.data
								store.dispatch(userActions.login(data))
							}
						}
					)

					error.config.headers.Authorization = 'Bearer '.concat(
						data.accessToken
					)

					loaderEnabled && store.dispatch(SiteCreators.hideLoading())

					resolve(
						axios(error.config).catch(() => {
							store.dispatch(userActions.logout())
							loaderEnabled && store.dispatch(SiteCreators.hideLoading())
							reject(error)
						})
					)
				})
				.catch(() => {
					store.dispatch(userActions.logout())
					loaderEnabled && store.dispatch(SiteCreators.hideLoading())
					reject(error)
				})
		} else {
			loaderEnabled && store.dispatch(SiteCreators.hideLoading())
			reject(error)
		}
	})
}

const handleSuccess = data => {
	const { errors } = data.data
	if (errors && errors.length && errors[0] != 'O Refresh token é inválido') {
		if (!toastIgnorePaths.includes(location.pathname))
			store.dispatch(toastActions.danger({ message: errors[0] }))
	}

	return data
}

export class AxiosService {
	static baseURL = config.apiUrl

	/**
	 * Initializes the axios configuration and add interceptors
	 */
	static init() {
		axios.interceptors.response.use(res => res, handleError)
	}

	/**
	 * The http get
	 * @param {*} url The server url
	 * @param {*} config The configuration of GET
	 */
	static get(url, config) {
		return axios
			.get(this.baseURL + url, Object.assign({}, getBaseConfig(), config))
	}

	/**
	 * GET that ignores new calls while a request with the same key is in flight (keep first)
	 * @param {string} key Unique identifier for the request
	 * @param {string} url The server url
	 * @param {*} config The configuration of GET
	 */
	static getKeepFirst(key, url, config) {
		if (pendingRequests[key]) return Promise.resolve({ data: null })

		pendingRequests[key] = true

		return this.get(url, config)
			.finally(() => {
				delete pendingRequests[key]
			})
	}

	/**
	 * GET that aborts any previous request with the same key (keep latest)
	 * @param {string} key Unique identifier for the request
	 * @param {string} url The server url
	 * @param {*} config The configuration of GET
	 */
	static getKeepLatest(key, url, config) {
		if (pendingRequests[key]) pendingRequests[key].abort()

		const controller = new AbortController()
		pendingRequests[key] = controller

		return this.get(url, Object.assign({}, config, { signal: controller.signal }))
			.finally(() => {
				if (pendingRequests[key] === controller) {
					delete pendingRequests[key]
				}
			})
	}

	/**
	 * The http post
	 * @param {string} url The server url
	 * @param {object} params The request data
	 * @param {object} config The configuration of POST
	 */
	static post(url, params, config) {
		return axios
			.post(
				this.baseURL + url,
				params,
				Object.assign({}, getBaseConfig(), config)
			)
			.then(handleSuccess)
	}

	/**
	 * The http put
	 * @param {string} url The server url
	 * @param {object} params The request data
	 * @param {object} config The configuration of POST
	 */
	static put(url, params, config) {
		return axios
			.put(
				this.baseURL + url,
				params,
				Object.assign({}, getBaseConfig(), config)
			)
			.then(handleSuccess)
	}
}
